import React, { useEffect, useState, useRef, Fragment } from "react";
import { useDispatch } from "react-redux";
import { Row, Col, Card, Button, Table } from "react-bootstrap";
import { Fn_FillListData, Fn_GetReport } from "../../store/Functions";
import { API_WEB_URLS } from "../../constants/constAPI";
import PageTitle from "../layouts/PageTitle";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from "exceljs";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const currentYear = new Date().getFullYear();
const YEAR_LIST = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

const Form7Report = () => {
  const dispatch = useDispatch();
  
  // Setup session state from localStorage
  const readSession = () => ({
    firm: (() => {
      try {
        return JSON.parse(localStorage.getItem("selectedFirm") || "null");
      } catch {
        return null;
      }
    })(),
    month: localStorage.getItem("selectedPayrollMonth") || "",
    year: parseInt(localStorage.getItem("selectedPayrollYear") || "0") || currentYear,
  });

  const [sessionFirm, setSessionFirm] = useState(() => readSession().firm);
  const [selectedMonth, setSelectedMonth] = useState(() => readSession().month);
  const [selectedYear, setSelectedYear] = useState(() => readSession().year);

  // Lists and loading states
  const [firmList, setFirmList] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Sync session changes from storage and sessionChange events
  useEffect(() => {
    const onStorage = () => {
      const s = readSession();
      setSessionFirm(s.firm);
      setSelectedMonth(s.month);
      setSelectedYear(s.year);
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("sessionChange", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("sessionChange", onStorage);
    };
  }, []);

  // Fetch initial base listings
  useEffect(() => {
    // Fetch firms list
    const firmApiUrl = `${API_WEB_URLS.MASTER}/0/token/${API_WEB_URLS.FirmMaster}/Id/0`;
    Fn_FillListData(dispatch, setFirmList, "gridData", firmApiUrl);
  }, [dispatch]);

  // Load report data when firm, month, or year changes
  useEffect(() => {
    const firmId = sessionFirm?.Id || 0;
    if (firmId && selectedMonth && selectedYear) {
      loadReportData(firmId, selectedMonth, selectedYear);
    } else {
      setReportData(null);
    }
  }, [sessionFirm, selectedMonth, selectedYear]);

  const loadReportData = async (firmId, monthName, year) => {
    setIsLoading(true);
    try {
      if (monthName && year) {
        const monthNum = MONTH_NAMES.indexOf(monthName) + 1;
        const monthStr = String(monthNum);

        const fd = new FormData();
        fd.append("F_FirmMaster", firmId);
        fd.append("JoinMonth", monthStr);
        fd.append("JoinYear", year);

        const records = await Fn_GetReport(
          dispatch,
          () => {},
          "data",
          "GetESIForm7/0/token",
          { arguList: { id: 0, formData: fd } },
          true
        );

        let parsedData = null;
        if (records && records.length > 0) {
          const firstKey = Object.keys(records[0])[0];
          if (firstKey !== undefined && firstKey !== null) {
            const jsonString = records.map(row => row[firstKey] || "").join("");
            if (jsonString) {
              const rawObj = JSON.parse(jsonString);
              parsedData = rawObj.data || rawObj;
            }
          }
        }
        setReportData(parsedData);
      } else {
        setReportData(null);
      }
    } catch (err) {
      console.error("Error loading report data:", err);
      setReportData(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Find ESI Code No. from selected firm's main branch or first branch
  const getEsiCodeNo = () => {
    if (!sessionFirm || !sessionFirm.BranchData) return "N/A";
    try {
      const branches = JSON.parse(sessionFirm.BranchData);
      if (Array.isArray(branches) && branches.length > 0) {
        const mainBranch = branches.find(b => b.IsMainBranch === 1) || branches[0];
        return mainBranch.ESINo || "N/A";
      }
    } catch (e) {
      console.error("Error parsing branch data:", e);
    }
    return "N/A";
  };

  const records = reportData?.Records || [];

  // Filter list by search term
  const filteredRecords = records.filter(rec => {
    const nameMatch = rec.EmployeeName?.toLowerCase().includes(searchTerm.toLowerCase());
    const fatherMatch = rec.FatherName?.toLowerCase().includes(searchTerm.toLowerCase());
    const esiMatch = rec.ESINo?.toLowerCase().includes(searchTerm.toLowerCase());
    return nameMatch || fatherMatch || esiMatch;
  });

  // Calculate Grand Totals
  const totalDays = filteredRecords.reduce((sum, r) => sum + parseFloat(r.PaidDays || 0), 0);
  const totalWages = filteredRecords.reduce((sum, r) => sum + parseFloat(r.Wages || 0), 0);
  const totalEsi = filteredRecords.reduce((sum, r) => sum + parseFloat(r.EmployeeESI || 0), 0);

  const rowsData = filteredRecords.map((rec, index) => {
    return {
      sno: index + 1,
      empId: rec.Sno,
      esiNo: rec.ESINo || "",
      name: rec.EmployeeName || "",
      fatherName: rec.FatherName || "",
      days: parseFloat(rec.PaidDays || 0),
      wages: parseFloat(rec.Wages || 0),
      esi: parseFloat(rec.EmployeeESI || 0),
      hasRecord: true
    };
  });

  // Excel Export
  const exportToExcel = async () => {
    if (rowsData.length === 0) {
      alert("No data available to export.");
      return;
    }

    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("ESI Form 7");

      // Title & Header Rows
      const titleRow = worksheet.addRow(["ESI CODE NO.:-", getEsiCodeNo(), "", "", "FOR THE MONTH OF " + (selectedMonth ? selectedMonth.toUpperCase() : "") + " " + selectedYear]);
      titleRow.font = { bold: true, size: 11 };
      worksheet.addRow([]); // empty row

      // Main Table Header structure
      const headerRow1 = worksheet.addRow(["S.N.O.", "ESI NO.", "EMPLOYEE NAME / FATHER'S NAME", "EMPLOYEERSE ESI AMOUNT", "", ""]);
      const headerRow2 = worksheet.addRow(["", "", "", "DAYS", "WAGES", "ESI"]);
      
      // Styling Headers
      [headerRow1, headerRow2].forEach(row => {
        row.font = { bold: true, color: { argb: "FFFFFFFF" } };
        row.eachCell(cell => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF2C3E50" }
          };
          cell.alignment = { vertical: "middle", horizontal: "center" };
          cell.border = {
            top: { style: "thin", color: { argb: "FF000000" } },
            left: { style: "thin", color: { argb: "FF000000" } },
            bottom: { style: "thin", color: { argb: "FF000000" } },
            right: { style: "thin", color: { argb: "FF000000" } }
          };
        });
      });

      // Merge header cells
      worksheet.mergeCells("A3:A4");
      worksheet.mergeCells("B3:B4");
      worksheet.mergeCells("C3:C4");
      worksheet.mergeCells("D3:F3");

      // Add Data Rows
      rowsData.forEach((row, idx) => {
        const r = worksheet.addRow([
          row.sno,
          row.esiNo,
          `${row.name}\n${row.fatherName}`,
          row.days,
          row.wages,
          row.esi || ""
        ]);
        
        r.getCell(3).alignment = { wrapText: true };
        r.eachCell(cell => {
          cell.border = {
            top: { style: "thin", color: { argb: "FFCCCCCC" } },
            left: { style: "thin", color: { argb: "FFCCCCCC" } },
            bottom: { style: "thin", color: { argb: "FFCCCCCC" } },
            right: { style: "thin", color: { argb: "FFCCCCCC" } }
          };
        });

        // Alternating colors
        if (idx % 2 === 1) {
          r.eachCell(cell => {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFF9FAFB" }
            };
          });
        }
      });

      // Total Row
      const totalR = worksheet.addRow([
        "TOTAL",
        "",
        "",
        totalDays,
        totalWages,
        totalEsi
      ]);
      totalR.font = { bold: true };
      worksheet.mergeCells(`A${totalR.number}:C${totalR.number}`);
      totalR.getCell(1).alignment = { horizontal: "right" };
      totalR.eachCell(cell => {
        cell.border = {
          top: { style: "thin", color: { argb: "FF000000" } },
          bottom: { style: "double", color: { argb: "FF000000" } }
        };
      });

      // Auto fit columns
      worksheet.columns.forEach((column, i) => {
        let maxLen = 15;
        column.eachCell({ includeEmpty: true }, cell => {
          if (cell.value) {
            const lines = cell.value.toString().split("\n");
            lines.forEach(l => {
              if (l.length > maxLen) maxLen = l.length;
            });
          }
        });
        column.width = i === 2 ? 35 : maxLen + 3;
      });

      // Download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `ESI_Form7_Report_${selectedMonth}_${selectedYear}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Excel export error:", error);
      alert("Error exporting to Excel. Please try again.");
    }
  };

  // PDF Export
  const exportToPDF = () => {
    if (rowsData.length === 0) {
      alert("No data available to export.");
      return;
    }

    const pdf = new jsPDF("p", "mm", "a4");

    // Add ESI Code and Month Title headers
    pdf.setFontSize(10);
    pdf.setFont("Helvetica", "bold");
    pdf.text(`ESI CODE NO.:- ${getEsiCodeNo()}`, 14, 15);
    pdf.text(`FOR THE MONTH OF ${(selectedMonth || "").toUpperCase()} ${selectedYear}`, 130, 15);

    // Prepare table body columns
    const tableBody = rowsData.map(row => [
      row.sno,
      row.esiNo,
      `${row.name}\n${row.fatherName}`,
      row.days.toFixed(2),
      row.wages.toFixed(2),
      row.esi > 0 ? row.esi.toFixed(2) : ""
    ]);

    // Push total row
    tableBody.push([
      { content: "TOTAL", colSpan: 3, styles: { halign: "right", fontStyle: "bold" } },
      totalDays.toFixed(2),
      totalWages.toFixed(2),
      totalEsi.toFixed(2)
    ]);

    autoTable(pdf, {
      head: [
        [
          { content: "S.N.O.", rowSpan: 2, styles: { valign: "middle", halign: "center" } },
          { content: "ESI NO.", rowSpan: 2, styles: { valign: "middle", halign: "center" } },
          { content: "EMPLOYEE NAME / FATHER'S NAME", rowSpan: 2, styles: { valign: "middle", halign: "left" } },
          { content: "EMPLOYEERSE ESI AMOUNT", colSpan: 3, styles: { halign: "center" } }
        ],
        ["DAYS", "WAGES", "ESI"]
      ],
      body: tableBody,
      startY: 22,
      styles: {
        fontSize: 8,
        cellPadding: 2.5,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        textColor: [0, 0, 0]
      },
      headStyles: {
        fillColor: [44, 62, 80],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        halign: "center"
      },
      columnStyles: {
        0: { halign: "center", cellWidth: 12 },
        1: { halign: "center", cellWidth: 28 },
        2: { halign: "left", cellWidth: 70 },
        3: { halign: "right", cellWidth: 22 },
        4: { halign: "right", cellWidth: 26 },
        5: { halign: "right", cellWidth: 22 }
      },
      theme: "grid"
    });

    pdf.save(`ESI_Form7_Report_${selectedMonth}_${selectedYear}.pdf`);
  };

  // Printable Area CSS Styles
  const printStyles = `
    @media print {
      body * {
        visibility: hidden !important;
      }
      #printable-area-container, #printable-area-container * {
        visibility: visible !important;
      }
      #printable-area-container {
        display: block !important;
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        padding: 20px;
        background: #fff !important;
        color: #000 !important;
        font-family: 'Courier New', Courier, monospace !important;
      }
      .no-print {
        display: none !important;
      }
      .card {
        border: none !important;
        box-shadow: none !important;
      }
      .table-print-layout {
        border-collapse: collapse !important;
        width: 100% !important;
        margin-top: 15px !important;
      }
      .table-print-layout th, .table-print-layout td {
        border: 1px solid #000 !important;
        color: #000 !important;
        padding: 6px 8px !important;
        font-size: 11px !important;
        background-color: transparent !important;
      }
      .table-print-layout th {
        font-weight: bold !important;
        text-align: center !important;
      }
      .table-print-layout td.text-end {
        text-align: right !important;
      }
      .print-header {
        display: flex !important;
        justify-content: space-between !important;
        border-bottom: 2px solid #000 !important;
        padding-bottom: 5px !important;
        margin-bottom: 15px !important;
        font-weight: bold !important;
        font-size: 12px !important;
      }
    }
  `;

  return (
    <Fragment>
      <style>{printStyles}</style>
      
      <div className="no-print">
        <PageTitle activeMenu="Form 7 Report" motherMenu="Reports" pageContent="Form 7 (ESI Register)" />
      </div>

      {/* Modern Filter / Search Card */}
      <Card className="no-print mb-4 shadow-sm border-0" style={{ borderRadius: "10px", background: "#ffffff" }}>
        <Card.Body className="p-4">
          <Row className="align-items-end g-3">
            {/* Firm Dropdown */}
            <Col lg={4} md={6}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>
                Firm <span className="badge bg-light text-success ms-1 border" style={{ fontSize: "11px" }}>🔒 Header Locked</span>
              </label>
              <input
                type="text"
                className="form-control"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px", backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                value={sessionFirm?.Name || "Please select Firm from Header"}
                readOnly
                disabled
              />
            </Col>

            {/* Month Dropdown */}
            <Col lg={2} md={3}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Month</label>
              <select
                className="form-select"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                value={selectedMonth}
                onChange={(e) => {
                  setSelectedMonth(e.target.value);
                  if (e.target.value) localStorage.setItem("selectedPayrollMonth", e.target.value);
                  else localStorage.removeItem("selectedPayrollMonth");
                }}
              >
                <option value="">-- Month --</option>
                {MONTH_NAMES.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </Col>

            {/* Year Dropdown */}
            <Col lg={2} md={3}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Year</label>
              <select
                className="form-select"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(parseInt(e.target.value) || 0);
                  if (e.target.value) localStorage.setItem("selectedPayrollYear", e.target.value);
                  else localStorage.removeItem("selectedPayrollYear");
                }}
              >
                {YEAR_LIST.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </Col>

            {/* Employee Filter */}
            <Col lg={4} md={12}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Search Employee</label>
              <div className="input-group" style={{ height: "38px" }}>
                <input
                  type="text"
                  className="form-control"
                  style={{ borderTopLeftRadius: "6px", borderBottomLeftRadius: "6px", fontSize: "13px" }}
                  placeholder="Search by name, father name, or ESI no..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button className="btn btn-outline-light border" type="button" onClick={() => setSearchTerm("")} style={{ background: "#fff", borderColor: "#ced4da" }}>
                    <i className="fa fa-times text-muted" />
                  </button>
                )}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Main Preview Container */}
      {!sessionFirm?.Id ? (
        <Card className="shadow-sm border-0" style={{ borderRadius: "10px" }}>
          <Card.Body className="text-center p-5">
            <h5 className="text-warning mb-2 fw-semibold">⚠ Session Setup Required</h5>
            <p className="text-muted mb-0 fs-13">
              Please select a **Firm**, **Month**, and **Year** from the filters above or from the **Dashboard Home** to load the report.
            </p>
          </Card.Body>
        </Card>
      ) : (
        <Fragment>
          {/* ─── WEB INTERFACE VIEW (Modern, Styled with Poppins) ─── */}
          <div className="no-print">
            <Card className="shadow-sm border-0" style={{ borderRadius: "10px", background: "#ffffff", overflow: "hidden" }}>
              <Card.Header className="d-flex justify-content-between align-items-center bg-white border-bottom py-3 px-4">
                <div>
                  <h5 className="mb-0 text-dark fw-bold fs-16" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    ESI Register (Form 7)
                  </h5>
                  <small className="text-muted fs-12">
                    ESI Code: <strong className="text-primary">{getEsiCodeNo()}</strong> &nbsp;|&nbsp; Period: <strong>{(selectedMonth || "").toUpperCase()} {selectedYear}</strong>
                  </small>
                </div>
                <div className="d-flex gap-2">
                  <Button variant="danger" size="sm" className="px-3" style={{ borderRadius: "5px", fontSize: "12px", background: "#E74C3C", border: "none" }} onClick={exportToPDF}>
                    <i className="fa fa-file-pdf-o me-2" />
                    PDF
                  </Button>
                  <Button variant="success" size="sm" className="px-3" style={{ borderRadius: "5px", fontSize: "12px", background: "#27AE60", border: "none" }} onClick={exportToExcel}>
                    <i className="fa fa-file-excel-o me-2" />
                    Excel
                  </Button>
                  <Button variant="primary" size="sm" className="px-3 text-white" style={{ borderRadius: "5px", fontSize: "12px", background: "#2980B9", border: "none" }} onClick={() => window.print()}>
                    <i className="fa fa-print me-2" />
                    Print
                  </Button>
                </div>
              </Card.Header>
              
              <Card.Body className="p-0">
                {isLoading ? (
                  <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status" />
                    <div className="text-muted mt-2 fs-13">Generating ESI Register...</div>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table hover striped className="mb-0 align-middle" style={{ fontSize: "13px", fontFamily: "'Poppins', sans-serif" }}>
                      <thead style={{ background: "#F8F9FA" }}>
                        <tr style={{ borderBottom: "2px solid #E9ECEF" }}>
                          <th rowSpan={2} className="text-center fw-bold" style={{ width: "60px", color: "#495057", verticalAlign: "middle" }}>S.No.</th>
                          <th rowSpan={2} className="text-center fw-bold" style={{ width: "160px", color: "#495057", verticalAlign: "middle" }}>ESI No.</th>
                          <th rowSpan={2} className="fw-bold" style={{ color: "#495057", verticalAlign: "middle" }}>Employee Name / Father's Name</th>
                          <th colSpan={3} className="text-center fw-bold" style={{ color: "#495057", borderBottom: "1px solid #E9ECEF" }}>Employee's ESI Details</th>
                        </tr>
                        <tr style={{ background: "#F8F9FA" }}>
                          <th className="text-center fw-semibold text-muted" style={{ width: "100px" }}>Days</th>
                          <th className="text-end fw-semibold text-muted" style={{ width: "140px" }}>Wages</th>
                          <th className="text-end fw-semibold text-muted" style={{ width: "120px" }}>ESI Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rowsData.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="text-center py-5 text-muted">
                              No employees under ESI coverage found for this firm.
                            </td>
                          </tr>
                        ) : (
                          rowsData.map((row) => (
                            <tr key={row.empId}>
                              <td className="text-center text-secondary">{row.sno}</td>
                              <td className="text-center fw-semibold text-dark">{row.esiNo}</td>
                              <td>
                                <div className="fw-bold text-dark">{row.name.toUpperCase()}</div>
                                <div className="text-muted fs-11" style={{ fontSize: "11px", color: "#6C757D" }}>
                                  {row.fatherName.toUpperCase()}
                                </div>
                              </td>
                              <td className="text-center text-dark fw-semibold">{row.days.toFixed(2)}</td>
                              <td className="text-end text-dark fw-bold">₹{row.wages.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                              <td className="text-end text-danger fw-bold">
                                {row.esi > 0 ? `₹${row.esi.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}
                              </td>
                            </tr>
                          ))
                        )}

                        {/* Grand Totals */}
                        {rowsData.length > 0 && (
                          <tr className="bg-light" style={{ borderTop: "2px solid #DEE2E6", fontWeight: "bold" }}>
                            <td colSpan={3} className="text-end text-dark fw-bold pe-4">GRAND TOTAL</td>
                            <td className="text-center text-dark fw-bold">{totalDays.toFixed(2)}</td>
                            <td className="text-end text-dark fw-bold">₹{totalWages.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td className="text-end text-danger fw-bold">₹{totalEsi.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>

          {/* ─── PHYSICAL PRINTING VIEW (Accurate Form 7 Document Casing) ─── */}
          <div id="printable-area-container" className="d-none">
            <div className="print-header">
              <div>
                ESI CODE NO.:- &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                <span style={{ textDecoration: "underline" }}>{getEsiCodeNo()}</span>
              </div>
              <div>
                FOR THE MONTH OF &nbsp;
                <span style={{ textDecoration: "underline" }}>{(selectedMonth || "").toUpperCase()} {selectedYear}</span>
              </div>
            </div>

            <table className="table-print-layout">
              <thead>
                <tr>
                  <th rowSpan={2} style={{ width: "5%" }}>S.N.<br />O.</th>
                  <th rowSpan={2} style={{ width: "15%" }}>ESI NO.</th>
                  <th rowSpan={2} style={{ width: "40%", textAlign: "left" }}>EMPLOYEE NAME<br />&nbsp;&nbsp;FATHER'S NAME</th>
                  <th colSpan={3} style={{ width: "40%" }}>EMPLOYEERSE ESI AMOUNT</th>
                </tr>
                <tr>
                  <th style={{ width: "12%" }}>DAYS</th>
                  <th style={{ width: "16%" }}>WAGES</th>
                  <th style={{ width: "12%" }}>ESI</th>
                </tr>
              </thead>
              <tbody>
                {rowsData.map((row) => (
                  <tr key={row.empId}>
                    <td style={{ textAlign: "center" }}>{row.sno}</td>
                    <td style={{ textAlign: "center" }}>{row.esiNo}</td>
                    <td>
                      <strong>{row.name.toUpperCase()}</strong>
                      <br />
                      <span style={{ fontSize: "10px", paddingLeft: "10px" }}>{row.fatherName.toUpperCase()}</span>
                    </td>
                    <td style={{ textAlign: "right" }}>{row.days.toFixed(2)}</td>
                    <td style={{ textAlign: "right" }} className="text-end">{row.wages.toFixed(2)}</td>
                    <td style={{ textAlign: "right" }} className="text-end">{row.esi > 0 ? row.esi.toFixed(2) : ""}</td>
                  </tr>
                ))}
                <tr style={{ fontWeight: "bold" }}>
                  <td colSpan={3} style={{ textAlign: "right" }}>TOTAL</td>
                  <td style={{ textAlign: "right" }}>{totalDays.toFixed(2)}</td>
                  <td style={{ textAlign: "right" }} className="text-end">{totalWages.toFixed(2)}</td>
                  <td style={{ textAlign: "right" }} className="text-end">{totalEsi.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Fragment>
      )}
    </Fragment>
  );
};

export default Form7Report;
